/**
 * MeasurementsPanel — histórico antropométrico do atleta (fonte única).
 *
 * EXTRAÍDO da MeasurementsTab local da AthleteProfilePage (07/08/2026) para
 * ser consumido também pela CoachAthleteDetailPage (aba "Físico") — mesma
 * razão da morte do TemplatePreview: duas árvores da mesma coisa divergem,
 * e a segunda fica sempre atrás.
 *
 * Dados: GET /roster/athletes/:id/measurements (+ /latest) — endpoints
 * dual-mode (aceitam athlete_id OU user_id) com SEC-001 no servidor.
 *
 * LIMITE DELIBERADO (registrado com o revisor): série temporal e valores,
 * SEM faixa de referência colorida, SEM meta, SEM alerta. Com adolescente
 * no elenco, "acima do ideal" em vermelho é exatamente o que não se
 * constrói — a interpretação é do treinador e do nutricionista.
 * Campos esparsos (envergadura, massa magra, IMC) aparecem quando houver
 * e somem quando não — ausência não vira zero.
 */
import React, { useState, useEffect } from 'react';
import { Ruler, TrendingUp } from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';
import { AZURE } from '../../services/api';
import BodyProjection3D from './BodyProjection3D';

const LABELS = {
  pt: {
    currentMeasurements: 'Medidas Atuais', physicalTests: 'Testes Físicos',
    measureHistory: 'Histórico de Medidas', weightEvolution: 'Evolução do Peso (kg)',
    metrics: {
      height: 'Altura', weight: 'Peso', wingspan: 'Envergadura', bodyFat: '% Gordura',
      leanMass: 'Massa Magra', muscleMass: 'Massa Muscular', bmi: 'IMC',
      boneMass: 'Massa Óssea', restingHr: 'FC Repouso', vo2max: 'VO₂ Máx',
      flexibility: 'Flexibilidade', grip: 'Preenção',
    },
    tableDate: 'Data', tableHeight: 'Altura', tableWeight: 'Peso', tableFat: '% Gord.',
    tableLean: 'Massa Magra', tableBmi: 'IMC', tableStaff: 'Resp.',
    noMeasurements: 'Nenhuma medida registrada.',
    noMeasurementsHint: 'Solicite ao preparador físico uma avaliação.',
  },
  en: {
    currentMeasurements: 'Current Measurements', physicalTests: 'Physical Tests',
    measureHistory: 'Measurement History', weightEvolution: 'Weight Evolution (kg)',
    metrics: {
      height: 'Height', weight: 'Weight', wingspan: 'Wingspan', bodyFat: '% Body Fat',
      leanMass: 'Lean Mass', muscleMass: 'Muscle Mass', bmi: 'BMI',
      boneMass: 'Bone Mass', restingHr: 'Resting HR', vo2max: 'VO₂ Max',
      flexibility: 'Flexibility', grip: 'Grip Strength',
    },
    tableDate: 'Date', tableHeight: 'Height', tableWeight: 'Weight', tableFat: '% Fat',
    tableLean: 'Lean Mass', tableBmi: 'BMI', tableStaff: 'By',
    noMeasurements: 'No measurements recorded.',
    noMeasurementsHint: 'Ask the S&C coach for an evaluation.',
  },
};

const formatDate = (iso, locale = 'pt-BR') => (iso ? new Date(iso).toLocaleDateString(locale) : '—');

const Skeleton = ({ h = 'h-20' }) => (
  <div className={`${h} w-full rounded-lg animate-pulse`} style={{ backgroundColor: VUKZ.primaryLight }} />
);

const MetricCard = ({ label, value, unit }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center">
    <p className="text-lg font-bold" style={{ color: VUKZ.primary }}>{value || '—'}</p>
    <p className="text-[10px] text-gray-400">{label}{unit ? ` (${unit})` : ''}</p>
  </div>
);

export default function MeasurementsPanel({ athleteId, clubId, gender, lang = 'pt' }) {
  const t = LABELS[lang] || LABELS.pt;
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const [measurements, setMeasurements] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [histRes, latestRes] = await Promise.allSettled([
          AZURE.get(`/roster/athletes/${athleteId}/measurements`, { params: { institution_id: clubId } }),
          AZURE.get(`/roster/athletes/${athleteId}/measurements/latest`, { params: { institution_id: clubId } }),
        ]);
        if (histRes.status === 'fulfilled') setMeasurements(histRes.value.data?.data || histRes.value.data || []);
        if (latestRes.status === 'fulfilled') setLatest(latestRes.value.data?.data || latestRes.value.data);
      } catch (e) {
        console.error('Measurements load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [athleteId, clubId]);

  if (loading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} />)}</div>;
  }

  return (
    <div className="space-y-5">
      {/* Última medição em destaque */}
      {latest ? (
        <>
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: VUKZ.border }}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t.currentMeasurements}</h4>
              <span className="text-[10px] text-gray-400">
                {formatDate(latest.measured_at || latest.created_at, locale)} • {latest.measured_by_name || 'Staff'}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <MetricCard label={t.metrics.height} value={latest.height_cm} unit="cm" />
              <MetricCard label={t.metrics.weight} value={latest.weight_kg} unit="kg" />
              <MetricCard label={t.metrics.wingspan} value={latest.wingspan_cm} unit="cm" />
              <MetricCard label={t.metrics.bodyFat} value={latest.body_fat_pct} unit="%" />
              <MetricCard label={t.metrics.leanMass} value={latest.lean_mass_kg} unit="kg" />
              <MetricCard label={t.metrics.muscleMass} value={latest.muscle_mass_kg} unit="kg" />
              <MetricCard label={t.metrics.bmi} value={latest.bmi} />
              <MetricCard label={t.metrics.boneMass} value={latest.bone_mass_kg} unit="kg" />
            </div>
          </div>

          {/* Testes físicos — só quando existirem */}
          {(latest.resting_hr || latest.vo2_max || latest.flexibility_cm) && (
            <div className="bg-white rounded-xl border p-5" style={{ borderColor: VUKZ.border }}>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">{t.physicalTests}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {latest.resting_hr && <MetricCard label={t.metrics.restingHr} value={latest.resting_hr} unit="bpm" />}
                {latest.vo2_max && <MetricCard label={t.metrics.vo2max} value={latest.vo2_max} unit="ml/kg/min" />}
                {latest.flexibility_cm && <MetricCard label={t.metrics.flexibility} value={latest.flexibility_cm} unit="cm" />}
                {latest.grip_strength_kg && <MetricCard label={t.metrics.grip} value={latest.grip_strength_kg} unit="kg" />}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 text-gray-400">
          <Ruler size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t.noMeasurements}</p>
          <p className="text-[10px] text-gray-400 mt-1">{t.noMeasurementsHint}</p>
        </div>
      )}

      {/* Histórico */}
      {measurements.length > 1 && (
        <div className="bg-white rounded-xl border p-5" style={{ borderColor: VUKZ.border }}>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1">
            <TrendingUp size={12} /> {t.measureHistory}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-[10px] font-bold text-gray-400 uppercase">
                  <th className="px-3 py-2">{t.tableDate}</th>
                  <th className="px-3 py-2">{t.tableHeight}</th>
                  <th className="px-3 py-2">{t.tableWeight}</th>
                  <th className="px-3 py-2">{t.tableFat}</th>
                  <th className="px-3 py-2">{t.tableLean}</th>
                  <th className="px-3 py-2">{t.tableBmi}</th>
                  <th className="px-3 py-2">{t.tableStaff}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {measurements.slice(0, 12).map((m, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{formatDate(m.measured_at || m.created_at, locale)}</td>
                    <td className="px-3 py-2">{m.height_cm || '—'}</td>
                    <td className="px-3 py-2">{m.weight_kg || '—'}</td>
                    <td className="px-3 py-2">{m.body_fat_pct || '—'}</td>
                    <td className="px-3 py-2">{m.lean_mass_kg || '—'}</td>
                    <td className="px-3 py-2">{m.bmi || '—'}</td>
                    <td className="px-3 py-2 text-gray-400">{m.measured_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Evolução do peso — barras simples, sem faixa/meta/alerta (ver header) */}
          {measurements.length > 2 && (
            <div className="mt-4">
              <p className="text-[10px] text-gray-400 mb-2">{t.weightEvolution}</p>
              <div className="h-20 flex items-end gap-1">
                {measurements.slice(0, 12).reverse().map((m, i) => {
                  const weights = measurements.filter((x) => x.weight_kg).map((x) => x.weight_kg);
                  const max = Math.max(...weights);
                  const min = Math.min(...weights);
                  const range = max - min || 1;
                  const pct = m.weight_kg ? ((m.weight_kg - min) / range) * 80 + 20 : 10;
                  return (
                    <div
                      key={i}
                      className="flex-1 rounded-t-sm"
                      style={{ height: `${pct}%`, backgroundColor: VUKZ.primary, opacity: 0.5 + (i / 24) }}
                      title={`${m.weight_kg}kg — ${formatDate(m.measured_at, locale)}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Projeção 3D (Digital Twin) */}
      {latest && (latest.height_cm || latest.weight_kg) && (
        <BodyProjection3D
          measurements={latest}
          gender={gender || latest.gender || 'male'}
          athleteName={''}
        />
      )}
    </div>
  );
}
